/**
 * Consistent zoom increment shared across every zoomable surface (FS-524).
 *
 * Each "+" / "-" click changes the *displayed* zoom by 1 percentage point.
 *
 * UI-28: this was 10pp (FS-524). The jump from 100% straight to 110% reads as
 * a lurch rather than a zoom, so the step is now the smallest unit the readout
 * can show. Reaching 200% takes proportionally more clicks; the wheel and the
 * type-in box remain the fast ways to cross a large range.
 * Surfaces define 100% differently — some use an absolute scale (board, image
 * preview, datasheet), others a fit-to-screen baseline (Knowledge Graph, P&ID)
 * — so each applies the step in its own units:
 *   - absolute-scale surfaces:  scale ± ZOOM_STEP
 *   - fit-baseline surfaces:    scale ± ZOOM_STEP * fitBaseline
 *   - percent-based surfaces:   percent ± ZOOM_STEP_PERCENT (PDF viewer)
 *
 * `steppedZoom` is the single source of truth for one additive +/- click.
 * Stepping additively (not multiplicatively, e.g. ×1.2/÷1.2) is what makes the
 * increment uniform and symmetric across pages.
 */

/**
 * One zoom step as a fraction of 100% (i.e. 10 percentage points).
 *
 * Back to 10 after #32242 took it to 0.01. That PR cited UI-28, but UI-28 is
 * Pegah asking for the count-up/down ANIMATION between values to go -- "it's
 * not consistent with other screens" -- not for the step to shrink. The
 * animation was the right thing to remove and is gone; the step went with it by
 * mistake, and 1pp per click reads as a dead button. Putra, 2026-09-17: "why 1%
 * change click plus and minus ... pegah just want the animation to be removed
 * ... the animation, not the scale".
 *
 * So: do not shrink this to make zoom feel finer. The wheel and the type-in box
 * are the fine controls; the +/- buttons are the coarse one, and a step a
 * reader cannot see is the same as no button at all.
 */
export const ZOOM_STEP = 0.1

/** The same step in whole percentage points, for percent-based surfaces. */
export const ZOOM_STEP_PERCENT = ZOOM_STEP * 100

interface SteppedZoomOptions {
  /** Scale that corresponds to 100% on this surface (1 for absolute-scale surfaces). */
  baseline?: number
  /** Lower clamp, in the surface's own scale units. */
  min?: number
  /** Upper clamp, in the surface's own scale units. */
  max?: number
}

/**
 * Returns the zoom value after one additive +/- click.
 *
 * @param current   current zoom in the surface's own scale units
 * @param direction +1 to zoom in, -1 to zoom out
 * @param options   baseline (the scale that reads as 100%) and optional clamps
 */
export function steppedZoom(
  current: number,
  direction: 1 | -1,
  { baseline = 1, min = -Infinity, max = Infinity }: SteppedZoomOptions = {}
): number {
  const base = baseline || 1
  const next = current + direction * ZOOM_STEP * base
  const clamped = Math.min(max, Math.max(min, next))
  // Round the dimensionless ratio (scale / baseline), NOT the raw scale. The
  // displayed % is `scale / baseline * 100`, so the ratio is always O(1)
  // (≈1.1, 0.9, …) and a 1e-3 grid on it stays fine relative to the step no
  // matter how small `baseline` is. Rounding the *raw scale* to 1e-3 swallowed
  // ~10% of each step when `baseline` was a large drawing's tiny fit scale
  // (~0.045): the readout moved 9pp per click, not 10pp — the step was 10pp
  // when FS-562 measured that; the shortfall scales with whatever the step is.
  // When `baseline == 1` (absolute surfaces) this is identical to the old
  // behavior, so repeated steps still land on a clean grid and "=== 1" stays
  // honest.
  return (Math.round((clamped / base) * 1000) / 1000) * base
}

/**
 * Max zoom-in on raster surfaces (P&ID, original document) as a multiple of the
 * document's native 1:1 resolution (a d3 transform `k` of 1 == native). 2 lets
 * users blow fine text up to twice its own pixels — comfortably readable — while
 * the readout still tops out at 200% (FS-560: "I want to read small text").
 */
export const ZOOM_MAX_NATIVE = 2

/**
 * The absolute max scale a fit-baseline surface zooms to: ZOOM_MAX_NATIVE× native
 * (k), but never less than 2× the fit `baseline` so small / already-enlarged
 * documents can still zoom in. `baseline` is the fit scale in the surface's units.
 */
export function fitZoomMaxScale(baseline: number): number {
  return Math.max(ZOOM_MAX_NATIVE, (baseline || 1) * 2)
}

/**
 * Displayed zoom % for a fit-baseline surface, calibrated so the fit `baseline`
 * reads 100% and `fitZoomMaxScale(baseline)` reads 200%. `scale` and `baseline`
 * are in the same units (e.g. a d3 transform `k`). Linear in `scale`, so a fixed
 * scale step (see `fitZoomStepBaseline`) moves the % by a fixed amount.
 */
export function fitZoomPercent(scale: number, baseline: number): number {
  const base = baseline || 1
  const span = fitZoomMaxScale(base) - base || 1
  return Math.round(100 + ((scale - base) / span) * 100)
}

/**
 * `baseline` to pass to `steppedZoom` so one +/- click moves `fitZoomPercent` by
 * exactly one step (`ZOOM_STEP_PERCENT`, 1pp): 100 clicks span the whole
 * fit → max range.
 */
export function fitZoomStepBaseline(baseline: number): number {
  const base = baseline || 1
  return fitZoomMaxScale(base) - base
}

/**
 * The fit-baseline scale that displays as `percent` — the inverse of
 * `fitZoomPercent`. Used by the type-in zoom input (FS-562) to turn a typed
 * percentage back into a surface scale. Linear, so it round-trips exactly:
 * `fitZoomPercent(percentToFitScale(p, base), base) === p`.
 */
export function percentToFitScale(percent: number, baseline: number): number {
  const base = baseline || 1
  const span = fitZoomMaxScale(base) - base || 1
  return base + ((percent - 100) / 100) * span
}

/* ------------------------------------------------------------------------- *
 * FS-562: canonical zoom boundaries + percent-space helpers.
 *
 * The reported bugs (zoom-in reaching ~3000%, the zoom-out readout not
 * decreasing) come from surfaces clamping in their own *scale* units while the
 * displayed % is derived from a fit baseline that can be far smaller. The fix
 * is to bound and step the *displayed percentage* — the number the user reads —
 * and only then map back to the surface's scale. These constants/helpers are
 * that single source of truth.
 * ------------------------------------------------------------------------- */

/**
 * Universal display floor (%). On the step grid so the last zoom-out click is a
 * clean step (… → 22 → 21 → 20), not a stubby remainder (FS-562, which hit this
 * at 30 → 25 on the 10pp step). Surfaces may pass a higher floor.
 */
export const ZOOM_MIN_PERCENT = 20

/**
 * Display ceiling (%) for raster surfaces (PDF, image, datasheet). They stay
 * sharp when blown up because they re-render at the new scale, so they may zoom
 * past the vector cap.
 */
export const ZOOM_MAX_PERCENT_RASTER = 400

/**
 * Display ceiling (%) for vector / fit-baseline surfaces (P&ID, Knowledge
 * Graph, board). Matches the 2× native / 200% readable-text cap from FS-560
 * (see `ZOOM_MAX_NATIVE`); going further just pixelates the vector render.
 */
export const ZOOM_MAX_PERCENT_VECTOR = 200

/**
 * Every zoom level between two percent bounds, one `ZOOM_STEP_PERCENT` apart,
 * as multiples of a surface's 100% baseline (UI-28).
 *
 * tldraw walks a `zoomSteps` array for its OWN zoom in/out — the keyboard
 * shortcut and the pinch gesture. The board had pinned that array to exactly
 * three entries (min, fit, max), so those gestures jumped 25% -> 100% -> 200%
 * while the on-screen +/- buttons moved one step at a time through `useZoom`.
 * One page, two behaviours, and the gesture was the one that lurched.
 *
 * Handing tldraw the full ladder makes both routes agree. The FIRST and LAST
 * entries are unchanged, which is what FS-562 relies on: they are the clamps
 * the readout reads exactly 25% and 200% at.
 */
export function zoomStepLadder(
  baseline: number,
  minPercent: number = ZOOM_MIN_PERCENT,
  maxPercent: number = ZOOM_MAX_PERCENT_VECTOR
): number[] {
  const steps: number[] = []
  for (
    let percent = minPercent;
    percent < maxPercent;
    percent += ZOOM_STEP_PERCENT
  ) {
    steps.push((percent / 100) * baseline)
  }
  // Land the ceiling exactly, whatever the step divides into.
  steps.push((maxPercent / 100) * baseline)
  return steps
}

/** Clamp a display percentage to `[min, max]`. */
export function clampPercent(
  percent: number,
  min: number = ZOOM_MIN_PERCENT,
  max: number = ZOOM_MAX_PERCENT_RASTER
): number {
  return Math.min(max, Math.max(min, percent))
}

/**
 * Snap a display percentage to the nearest whole zoom step
 * (`ZOOM_STEP_PERCENT`, 1pp) so a typed value lands on the same grid as the
 * +/- buttons (FS-562: round increments).
 */
export function snapPercentToStep(percent: number): number {
  return Math.round(percent / ZOOM_STEP_PERCENT) * ZOOM_STEP_PERCENT
}

/* ------------------------------------------------------------------------- *
 * FS-562 follow-up: deep zoom for the raster-backed P&ID editor surfaces.
 *
 * The plain linear ratio model (`scale / fit × 100`) gave uniform steps and
 * zoom-out below 100%, but it also capped zoom-in at 2× the *fit* scale. For a
 * large drawing whose fit scale is ~0.2, that is k ≈ 0.4 — well below native
 * resolution (k = 1) — so fine text stayed unreadable even at "200%".
 *
 * This piecewise map keeps the ratio model at/below 100% (uniform zoom-out,
 * floor at ZOOM_MIN_PERCENT, unchanged from the QA round) and re-anchors the
 * top of the range: 200% == `fitZoomMaxScale(fit)` — 2× native pixels (FS-560's
 * "read small text" cap). Above 100% the map is geometric, so every +1pp click
 * multiplies the scale by the same factor: perceptually uniform steps, instead
 * of the front-loaded jumps a linear map across the same span produces (the
 * original QA complaint about the compressed fit→2×native model).
 * ------------------------------------------------------------------------- */

/**
 * Displayed zoom % for a deep-zoom surface. ≤ `baseline` it is the linear
 * ratio; above, a geometric ramp that reads 200% at `fitZoomMaxScale(baseline)`.
 * Unrounded — callers round for display. Inverse of `percentToDeepZoomScale`.
 */
export function deepZoomPercent(scale: number, baseline: number): number {
  const base = baseline || 1
  if (scale <= base) return (scale / base) * 100
  // fitZoomMaxScale ≥ 2×base, so the ratio is ≥ 2 and the log is well-defined.
  const ratio = fitZoomMaxScale(base) / base
  return 100 + (100 * Math.log(scale / base)) / Math.log(ratio)
}

/**
 * The deep-zoom surface scale that displays as `percent` — the inverse of
 * `deepZoomPercent`, used by the +/- steps and the type-in input. Linear at or
 * below 100%, geometric above, `fitZoomMaxScale(baseline)` at 200%.
 */
export function percentToDeepZoomScale(
  percent: number,
  baseline: number
): number {
  const base = baseline || 1
  if (percent <= 100) return (percent / 100) * base
  const ratio = fitZoomMaxScale(base) / base
  return base * Math.pow(ratio, (percent - 100) / 100)
}
